import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { experiences } from '../data/experience';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { getTechIcon } from '../utils/techIcons';
import { FaDatabase } from 'react-icons/fa';

const Experience: React.FC = () => {
  const { t } = useLanguage();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Figma 등으로 캡쳐할 때 모든 토글을 연 상태로 보고 싶다면
  // 배포된 URL 뒤에 ?open=all 을 붙이면 전체가 펼쳐지게 함
  const openAll =
    typeof window !== 'undefined' &&
    window.location.search.includes('open=all');

  return (
    <section id="experience" className="py-20 px-6">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-gradient">{t('experience.title')}</span>
          </h2>
          <p className="text-gray-400">{t('experience.subtitle')}</p>
        </motion.div>

        <div className="space-y-6">
          {experiences.map((exp, expIndex) => (
            <motion.div
              key={expIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: expIndex * 0.1 }}
              className="bg-dark-card rounded-lg p-6 shadow-xl"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-white">{exp.company}</h3>
                    {exp.type && (
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        exp.type === '외주'
                          ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                          : exp.type === '인턴'
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                          : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      }`}>
                        {t(`experience.type.${exp.type === '외주' ? 'freelance' : exp.type === '인턴' ? 'intern' : 'regular'}`)}
                      </span>
                    )}
                  </div>
                  <p className="text-lg text-gray-300 mb-1">{exp.position}</p>
                  <p className="text-sm text-gray-400">{exp.period}</p>
                </div>
                {!openAll && (
                  <button
                    onClick={() =>
                      setExpandedIndex(
                        expandedIndex === expIndex ? null : expIndex
                      )
                    }
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {expandedIndex === expIndex ? (
                      <HiChevronUp className="text-2xl" />
                    ) : (
                      <HiChevronDown className="text-2xl" />
                    )}
                  </button>
                )}
              </div>

              <p className="text-gray-300 mb-4">{exp.description}</p>

              <AnimatePresence>
                {(openAll || expandedIndex === expIndex) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 space-y-6 border-t border-gray-700">
                      {exp.projects.map((project, projIndex) => (
                        <motion.div
                          key={projIndex}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: projIndex * 0.1 }}
                          className="bg-dark-surface rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-xl font-semibold text-white">
                              {project.title}
                            </h4>
                            {project.period && (
                              <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs whitespace-nowrap">
                                ⏱️ {project.period}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-300 mb-3">{project.description}</p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {project.technologies.map((tech, techIndex) => {
                              const TechIcon = getTechIcon(tech) || FaDatabase;
                              return (
                                <motion.div
                                  key={techIndex}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: techIndex * 0.05 }}
                                  className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm group hover:bg-blue-500/30 transition-colors"
                                >
                                  <TechIcon className="text-base group-hover:scale-110 transition-transform" />
                                  <span>{tech}</span>
                                </motion.div>
                              );
                            })}
                          </div>
                          {project.achievements && (
                            <ul className="list-disc list-inside text-gray-400 space-y-1">
                              {project.achievements.map((achievement, achIndex) => (
                                <li key={achIndex}>{achievement}</li>
                              ))}
                            </ul>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
